<template>
  <v-row>
    <v-col>
      <div>
        <!-- Action buttons header -->
        <div class="d-flex">
          <v-btn  
            color="primary" 
            v-if="hasRunDetails" 
            class="mr-1 mb-2 white--text" 
            @click="$emit('open-reconciliation-dialogue')"
          >
            <v-icon class="mr-1">mdi-plus</v-icon><span>New</span>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn 
            color="primary" 
            class="mr-2 mb-2 shadow-off white--text"  
            @click="$emit('download-result', 'Reconciliation')"
          >
            <v-icon class="mr-1">mdi-file-excel</v-icon> <span>Download Excel</span>
          </v-btn>
        </div>

        <!-- Two File reconciliation view -->
        <div class="my-2" v-if="fileCount === TM_RECONCILIATION.FILE_TYPES.TWO_FILE">
          <!-- GL/SL Switcher - Only show for AR/AP analysis types -->
          <div class="mb-3 d-flex align-center" v-if="isARorAPAnalysis">
            <div class="gl-sl-switcher">
              <button 
                :class="['switcher-btn', viewMode === 'GL' ? 'active' : '']"
                @click="viewMode = 'GL'"
              >
                GL
              </button>
              <button 
                :class="['switcher-btn', viewMode === 'SL' ? 'active' : '']"
                @click="viewMode = 'SL'"
              >
                SL
              </button>
            </div>
            
            <!-- Filter dropdown for SL mode -->
            <div v-if="viewMode === 'SL' && formattedSummaryCombo.length > 0" class="ml-4" style="width: 300px;">
              <filter-dropdown-with-search
                v-model="selectedSummaryComboTwoFile"
                :items="formattedSummaryCombo"
                item-text="displayText"
                item-value="value"
                label="Filter By"
                placeholder="Filter By"
              />
            </div>
          </div>

          <!-- Loading spinner -->
          <div v-if="loading" class="text-center pa-6">
            <v-progress-circular
              indeterminate
              color="primary"
              size="50"
            ></v-progress-circular>
            <div class="mt-3 grey--text">Loading reconciliation data...</div>
          </div>
          
          <!-- Data table -->
          <v-simple-table 
            v-else-if="groupedReconciliationData.length > 0"
            dense 
            class="reconciliation-table border-on radius-small"
          >
            <template v-slot:default>
              <thead class="tableHeaderBg">
                <tr class="table-header-row tableHeaderBg">
                  <th class="col-description" id="description">Description</th>
                  <th class="col-sub-amount" id="subamount">Sub Amount</th>
                  <th class="col-final-amount" id="amount">Final Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr 
                  v-for="(item, k) in groupedReconciliationData" 
                  :key="item._key || item._id || k" 
                  :id="'automation-test-'+k"
                  :class="getRowClass(item)"
                >
                  <!-- Group Header Row -->
                  <template v-if="item._isGroupHeader">
                    <td colspan="3" class="group-header-cell">
                      {{ item._groupHeaderText }}
                    </td>
                  </template>
                  <!-- Regular Data Row -->
                  <template v-else>
                    <td :class="{ 'group-item-indent': item._isGroupItem }">
                      {{ item.Reconciliation }}
                      <span v-if="item.Group === 'Table 1 Total'" class="table-total-badge">Recon Amount</span>
                    </td>
                    <td>{{ formatNumberLocal(item['Sub Amount']) }}</td>
                    <td>{{ formatNumberLocal(item['Amount']) }}</td>
                  </template>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          
          <!-- No data message -->
          <div v-else class="text-center grey--text title pa-6">No data available</div>
        </div>

        <!-- Single File reconciliation view -->
        <div v-else>
          <div>
            <v-row class="mt-3">
              <!-- Filter dropdown column -->
              <v-col cols="2">
                <div v-if="summaryCombo.length > 0">
                  <lb-dropdown 
                    hidedetails 
                    label="Reconciliation Filter" 
                    class="flex-grow-1 my-0 py-0" 
                    :items="summaryCombo" 
                    v-model="selectedReconciliationFilterLocal" 
                    itemtext="combo-code" 
                    itemvalue="combo-code" 
                  />
                  <v-list 
                    dense 
                    class="my-0 py-0" 
                    max-height="400px" 
                    style="overflow-y: auto;"
                  >
                    <v-list-item-group v-model="selectedReconciliationLocal" class="my-0 py-0">
                      <v-list-item 
                        v-for="(item, i) in summaryCombo" 
                        :key="i" 
                        :value="i" 
                        :id="'automation-test-'+ i"
                      >
                        <v-list-item-content :id="'automation-test-'+i">
                          <v-list-item-title>{{ item['combo-code'] }}</v-list-item-title>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list-item-group>
                  </v-list>
                </div>
              </v-col>

              <!-- Data table column -->
              <v-col cols="10">
                <!-- Loading spinner -->
                <div v-if="loading" class="text-center pa-6">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    size="50"
                  ></v-progress-circular>
                  <div class="mt-3 grey--text">Loading reconciliation data...</div>
                </div>
                
                <!-- Data table -->
                <v-simple-table 
                  v-else-if="groupedReconciliationData.length > 0"
                  dense 
                  class="reconciliation-table border-on radius-small"
                >
                  <template v-slot:default>
                    <thead class="tableHeaderBg">
                      <tr class="table-header-row tableHeaderBg">
                        <th class="col-description">Description</th>
                        <th class="col-sub-amount">Sub Amount</th>
                        <th class="col-final-amount">Final Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, k) in groupedReconciliationData" :key="item._key || item._id || k"
                          :class="getRowClass(item)">
                        <!-- Group Header Row -->
                        <template v-if="item._isGroupHeader">
                          <td colspan="3" class="group-header-cell">
                            {{ item._groupHeaderText }}
                          </td>
                        </template>
                        <!-- Regular Data Row -->
                        <template v-else>
                          <!-- Description Column -->
                          <td :class="{ 'group-item-indent': item._isGroupItem }">
                            <!-- Handle Amount as string (parsed JSON with ADD/LESS structure) -->
                            <div v-if="typeof item.Amount === 'string' && item.Amount.includes('ADD')">
                              <div v-for="(values, key) in parsedAmount(item.Amount)" :key="key">
                                <!-- ADD section -->
                                <div v-if="key === 'ADD' && values.length">
                                  <div v-for="(subItem, index) in values" :key="index">
                                    <div v-for="(value, key) in subItem" :key="key">
                                      Add: {{ key }}
                                    </div>
                                  </div>
                                </div>
                                <!-- LESS section -->
                                <div v-if="key === 'LESS' && values.length">
                                  <div v-for="(subItem, index) in values" :key="index">
                                    <div v-for="(value, key) in subItem" :key="key">
                                      Less: {{ key }}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <!-- Regular description -->
                            <div v-else>
                              {{ item.Reconciliation || '' }}
                              <span v-if="item.Group === 'Table 1 Total'" class="table-total-badge">Recon Amount</span>
                            </div>
                          </td>

                          <!-- Sub Amount Column -->
                          <td>
                            <!-- Handle Amount as string (parsed JSON with ADD/LESS structure) -->
                            <div v-if="typeof item.Amount === 'string' && item.Amount.includes('ADD')">
                              <div v-for="(values, key) in parsedAmount(item.Amount)" :key="key">
                                <div v-if="key === 'ADD' && values.length">
                                  <div v-for="(subItem, index) in values" :key="index">
                                    <div v-for="(value, key) in subItem" :key="key">
                                      {{ formatNumberLocal(value) }}
                                    </div>
                                  </div>
                                </div>
                                <div v-if="key === 'LESS' && values.length">
                                  <div v-for="(subItem, index) in values" :key="index">
                                    <div v-for="(value, key) in subItem" :key="key">
                                      {{ formatNumberLocal(value) }}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <!-- Regular sub amount -->
                            <div v-else>
                              {{ formatNumberLocal(item['Sub Amount']) }}
                            </div>
                          </td>

                          <!-- Final Amount Column -->
                          <td>
                            <!-- Handle Amount as string (parsed JSON with ADD/LESS structure) -->
                            <div v-if="typeof item.Amount === 'string' && item.Amount.includes('ADD')">
                              <div v-for="(values, key) in parsedAmount(item.Amount)" :key="key">
                                <div v-if="key === 'ADD' && values.length">
                                  <div v-for="(subItem, index) in values" :key="index">
                                    <div v-for="(value, key) in subItem" :key="key">
                                      {{ formatNumberLocal(value) }}
                                    </div>
                                  </div>
                                </div>
                                <div v-if="key === 'LESS' && values.length">
                                  <div v-for="(subItem, index) in values" :key="index">
                                    <div v-for="(value, key) in subItem" :key="key">
                                      {{ formatNumberLocal(value) }}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <!-- Handle Amount as object with ADD property -->
                            <div v-else-if="item.Amount && typeof item.Amount === 'object' && item.Amount.ADD !== undefined">
                              {{ formatNumberLocal(item.Amount.ADD) }}
                            </div>
                            <!-- Regular amount (number) -->
                            <div v-else>
                              {{ formatNumberLocal(item['Amount']) }}
                            </div>
                          </td>
                        </template>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
                
                <!-- No data message -->
                <div v-else class="text-center grey--text title pa-6">No data available</div>
              </v-col>
            </v-row>
          </div>
        </div>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import FilterDropdownWithSearch from '@/views/common/FilterDropdownWithSearch.vue';
import { TM_RECONCILIATION, ERROR_MESSAGES } from '../../utils/constants';
import { groupReconciliationData } from '../../utils/utils';

export default {
  name: 'TransactionMatchingReconciliation',
  
  components: {
    FilterDropdownWithSearch
  },
  
  props: {
    // File count type (Two File or Single File)
    fileCount: {
      type: String,
      required: true
    },
    // Run details object to check if analysis has been run
    runDetails: {
      type: Object,
      default: () => ({})
    },
    // Analysis details including bpid
    analysisDetails: {
      type: Object,
      default: () => ({})
    },
    // Verification list for analysis runs
    verificationList: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      TM_RECONCILIATION,
      // Local copy of filter selection to avoid mutating prop
      selectedReconciliationFilterLocal: '',
      // Local copy of reconciliation selection
      selectedReconciliationLocal: 0,
      // Combo annexure data
      comboAnnexure: [],
      // Collection name list
      collectionNameList: [],
      // View mode for Two File (GL or SL)
      viewMode: TM_RECONCILIATION.VIEW_MODES.GL,
      // Selected summary combo for Two File SL mode
      selectedSummaryComboTwoFile: null
    };
  },

  computed: {
    ...mapGetters('tmReconciliation', [
      'summaryCombo',
      'reconciliationSummaryData',
      'reconciliationCollection',
      'loading'
    ]),
    
    /**
     * Check if run details exist to enable/disable New button
     */
    hasRunDetails() {
      return Object.keys(this.runDetails).length > 0;
    },
    
    /**
     * Check if analysis type is AR or AP to show GL/SL switcher
     */
    isARorAPAnalysis() {
      return this.analysisDetails.typeofanalysis === TM_RECONCILIATION.ANALYSIS_TYPES.ACCOUNTS_RECEIVABLE || 
             this.analysisDetails.typeofanalysis === TM_RECONCILIATION.ANALYSIS_TYPES.ACCOUNTS_PAYABLE;
    },
    
    /**
     * Format summary combo items for display in dropdown
     * Maps 'overallreconsummary' to 'Reconciliation' for better readability
     * Filters out duplicate 'Reconciliation' header entries
     * Ensures 'Reconciliation' appears first in the list
     */
    formattedSummaryCombo() {
      const formatted = this.summaryCombo
        // Filter out the 'Reconciliation' header entry (added by store) since we transform 'overallreconsummary' to display as 'Reconciliation'
        .filter(item => {
          const comboCode = (item['combo-code'] || '').trim().toLowerCase();
          return comboCode !== 'reconciliation';
        })
        .map(item => {
          const comboCode = item['combo-code'] || '';
          let displayText = comboCode;
          
          // Map technical names to user-friendly display text
          if (comboCode.toLowerCase() === 'overallreconsummary') {
            displayText = 'Reconciliation';
          }
          
          return {
            ...item,
            value: comboCode,
            displayText: displayText
          };
        });
      
      // Sort to ensure 'Reconciliation' (overallreconsummary) appears first
      return formatted.sort((a, b) => {
        if (a.displayText === 'Reconciliation') return -1;
        if (b.displayText === 'Reconciliation') return 1;
        return 0;
      });
    },
    
    /**
     * Grouped reconciliation summary data with group headers
     * Uses the common groupReconciliationData utility function
     */
    groupedReconciliationData() {
      return groupReconciliationData(this.reconciliationSummaryData, {
        groups: TM_RECONCILIATION.GROUPS,
        groupDisplayOrder: TM_RECONCILIATION.GROUP_DISPLAY_ORDER
      });
    }
  },

  watch: {
    /**
     * Watch for changes in reconciliation filter dropdown
     */
    selectedReconciliationFilterLocal(newValue) {
      if (newValue === null || newValue === undefined) {
        return;
      } else if (newValue === TM_RECONCILIATION.SUMMARY_TYPES.RECONCILIATION) {
        this.reconSummaryResult(this.reconciliationCollection);
        this.selectedReconciliationLocal = 0;
      } else {
        const index = this.summaryCombo.findIndex(item => item['combo-code'] === newValue);
        this.selectedReconciliationLocal = index;
        this.reconSummaryResultCombo(this.reconciliationCollection, newValue);
      }
    },

    /**
     * Watch for changes in reconciliation list selection
     */
    selectedReconciliationLocal(newValue) {
      if (newValue === null || newValue === undefined) {
        return;
      } else if (newValue === 0) {
        this.reconSummaryResult(this.reconciliationCollection);
      } else {
        this.reconSummaryResultCombo(this.reconciliationCollection, this.summaryCombo[newValue]['combo-code']);
      }
    },

    /**
     * Watch for changes in Two File summary combo selection (SL mode)
     */
    selectedSummaryComboTwoFile(newValue) {
      if (newValue === null || newValue === undefined) {
        return;
      } else {
        this.reconSummaryResultCombo(this.reconciliationCollection, newValue);
      }
    },

    /**
     * Watch for changes in view mode (GL/SL switcher)
     * GL mode: Show overall reconciliation summary
     * SL mode: Show first combo-code specific summary
     */
    viewMode(newValue) {
      if (newValue === TM_RECONCILIATION.VIEW_MODES.GL) {
        // Switch to GL: Reset selection and show overall summary
        this.selectedSummaryComboTwoFile = null;
        this.reconSummaryResult(this.reconciliationCollection);
      } else if (newValue === TM_RECONCILIATION.VIEW_MODES.SL) {
        // Switch to SL: Select first combo-code (skip "Reconciliation " header at index 0)
        if (this.summaryCombo.length > 1) {
          this.selectedSummaryComboTwoFile = this.summaryCombo[1]['combo-code'];
        }
      }
    },
    
    /**
     * Watch for changes in analysis type to enforce GL mode for non-AR/AP types
     */
    isARorAPAnalysis(newValue) {
      if (!newValue) {
        // If not AR or AP, force GL mode
        this.viewMode = TM_RECONCILIATION.VIEW_MODES.GL;
      }
    }
  },

  methods: {
    ...mapActions('tmReconciliation', [
      'fetchOverallReconSummary',
      'fetchComboReconSummary',
      'fetchUniqueComboCodes'
    ]),
    
    /**
     * Public method to initialize reconciliation data
     * Called from parent component when step becomes active
     */
    initializeReconciliation() {
      this.reconSummary();
    },
    
    /**
     * Initialize reconciliation summary data based on file count
     * Called in mounted() lifecycle hook
     */
    async reconSummary() {
      if (this.fileCount === TM_RECONCILIATION.FILE_TYPES.TWO_FILE) {
        // Two File logic
        let selectedVerification = this.verificationList[0] || {};
        let rundata = this.runDetails[selectedVerification._id] || {};
        this.comboAnnexure = rundata.annexure || [];
        
        if (this.comboAnnexure.length > 0) {         
          // First: Find and fetch reconciliation summary for GL view (default)
          // Support both old (typo) and new (correct) spelling for backward compatibility
          let reconCollection = this.comboAnnexure.filter(item => 
            item.name === TM_RECONCILIATION.ANNEXURE_NAMES.RECONCILIATION_SUMMARY ||
            item.name === TM_RECONCILIATION.ANNEXURE_NAMES.RECONCILIATION_SUMMARY_LEGACY
          );
          if (reconCollection.length > 0) {
            await this.reconSummaryResult(reconCollection[0].collection);
          }
          
          // Second: Conditionally fetch unique combo codes for SL view dropdown
          // Only for AR or AP Reconciliation analysis types
          if (this.isARorAPAnalysis) {
            let comboCollection = this.comboAnnexure.filter(item => 
              item.collection && item.collection.includes(TM_RECONCILIATION.COLLECTION_FILTERS.ANNEXURE_RECON_SUMMARY)
            );
            if (comboCollection.length > 0) {
              await this.generateAnnexureListSummaryCombo(
                comboCollection[0]?.collection, 
                TM_RECONCILIATION.SUMMARY_TYPES.RECONCILIATION
              );
              
              // After loading combo codes, if there are items and view mode is SL, select first combo
              if (this.viewMode === TM_RECONCILIATION.VIEW_MODES.SL && this.summaryCombo.length > 1) {
                // Skip the first item (which is "Reconciliation ") and select the second item
                this.selectedSummaryComboTwoFile = this.summaryCombo[1]['combo-code'];
              }
            }
          } else {
            // For non-AR/AP types, ensure GL mode is active
            this.viewMode = TM_RECONCILIATION.VIEW_MODES.GL;
          }
        }
      } else {
        // Single File logic
        let selectedVerification = this.verificationList[0] || {};
        let rundata = this.runDetails[selectedVerification._id] || {};
        this.comboAnnexure = rundata.annexure || [];
        
        if (this.comboAnnexure.length > 0) {
          let collectionSummary = this.comboAnnexure.filter(item => 
            item.collection && item.collection.includes(TM_RECONCILIATION.COLLECTION_FILTERS.ANNEXURE_SUMMARY)
          );
          if (collectionSummary.length > 0) {
            this.generateAnnexureListSummaryCombo(
              collectionSummary[0]?.collection, 
              TM_RECONCILIATION.SUMMARY_TYPES.RECONCILIATION
            );
          }
          collectionSummary = this.comboAnnexure.filter(item => 
            item.name === TM_RECONCILIATION.ANNEXURE_NAMES.RECONCILIATION_SUMMARY
          );

          if (collectionSummary.length > 0) {
            this.reconSummaryResult(collectionSummary[0].collection);
          }
        } else {
          this.$store.commit("sbError", ERROR_MESSAGES.ANALYSIS_NOT_RUN);
        }
      }
    },

    /**
     * Fetch reconciliation summary result from API
     * @param {string} collection - Collection name to query
     */
    async reconSummaryResult(collection) {
      await this.fetchOverallReconSummary({ collection });
      // Apply indexOrder and removeExcluded after data is fetched
      const orderedData = this.indexOrder(this.reconciliationSummaryData);
      this.$store.commit('tmReconciliation/SET_RECONCILIATION_SUMMARY_DATA', orderedData);
      this.removeExcluded();
    },

    /**
     * Fetch combo-specific reconciliation summary from API
     * @param {string} collection - Collection name to query
     * @param {string} combo - Combo code to filter by
     */
    async reconSummaryResultCombo(collection, combo) {
      await this.fetchComboReconSummary({ collection, combo });
      // Apply indexOrder and removeExcluded after data is fetched
      const orderedData = this.indexOrder(this.reconciliationSummaryData);
      this.$store.commit('tmReconciliation/SET_RECONCILIATION_SUMMARY_DATA', orderedData);
      this.removeExcluded();
    },

    /**
     * Generate annexure list summary combo dropdown
     * @param {string} collection - Collection name
     * @param {string} type - Type of summary (Result or Reconciliation)
     */
    async generateAnnexureListSummaryCombo(collection, type) {
      await this.fetchUniqueComboCodes({ collection, type });
    },

    /**
     * Sort items by index property if all items have it
     * @param {Array} items - Array of items to sort
     * @returns {Array} Sorted array or original array if not all have index
     */
    indexOrder(items) {
      if (items.length > 0 && items.every(item => item.index !== undefined && item.index !== null)) {
        return items.sort((a, b) => a.index - b.index);
      }
      return items;
    },

    /**
     * Remove excluded items from reconciliation summary data
     * Filters out items where Reconciliation === "Total Excluded" and Amount === "{'LESS': [], 'ADD': []}"
     */
    removeExcluded() {
      this.$store.commit('tmReconciliation/SET_RECONCILIATION_SUMMARY_DATA', 
        this.reconciliationSummaryData.filter(item => {
          return !(item.Reconciliation === TM_RECONCILIATION.EXCLUDED_FILTER.RECONCILIATION && 
                   item.Amount === TM_RECONCILIATION.EXCLUDED_FILTER.AMOUNT);
        })
      );
    },

    /**
     * Format number for local display, handling null/undefined values
     * @param {number} n - Number to format
     * @returns {string} Formatted number or empty string
     */
    formatNumberLocal(n) {
      if (n === null || n === undefined || n === '') {
        return '';
      }
      return this.$nova.formatNumber(n);
    },

    /**
     * Parse amount string from backend (JSON with single quotes)
     * @param {string} amount - Amount string to parse
     * @returns {object} Parsed amount object with ADD/LESS properties
     */
    parsedAmount(amount) {
      // Handle null, undefined, or empty string
      if (!amount || amount.trim() === '') {
        return { ADD: [], LESS: [] };
      }
      
      try {
        return JSON.parse(amount.replace(/'/g, '"'));
      } catch (e) {
        console.error('Error parsing Amount:', e);
        return { ADD: [], LESS: [] };
      }
    },

    /**
     * Get CSS class for a row based on its group and header status
     * @param {Object} item - Row item with _isGroupHeader and Group properties
     * @returns {Object} Class object for Vue :class binding
     */
    getRowClass(item) {
      return {
        'group-header-row': item._isGroupHeader,
        'table-total-row': item.Group === TM_RECONCILIATION.GROUPS.TABLE_1_TOTAL,
        'table-2-total-row': item.Group === TM_RECONCILIATION.GROUPS.TABLE_2_TOTAL,
        'group-item-row': item._isGroupItem
      };
    }
  }
};
</script>

<style scoped>
.gl-sl-switcher {
  display: inline-flex;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #183440;
}

.switcher-btn {
  padding: 8px 24px;
  background-color: white;
  color: #183440;
  border: none;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.3s ease;
  min-width: 60px;
}

.switcher-btn:first-child {
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
}

.switcher-btn:last-child {
  border-top-right-radius: 20px;
  border-bottom-right-radius: 20px;
}

.switcher-btn:not(:last-child) {
  border-right: 1px solid #183440;
}

.switcher-btn:hover {
  background-color: #f5f5f5;
}

.switcher-btn.active {
  background-color: #183440;
  color: white;
}

.switcher-btn:focus {
  outline: none;
}

/* Table header styling */
::v-deep .v-data-table thead tr th {
  background-color: #e5e5e5 !important;
}

::v-deep .grayBackground {
  background-color: #e5e5e5 !important;
}

/* Table row styling */
::v-deep .v-data-table tbody tr {
  background-color: #FFF !important;
}

::v-deep .v-simple-table tbody tr {
  background-color: #FFF !important;
}

/* Table row hover styling */
::v-deep .v-data-table tbody tr:hover {
  background-color: #F7F7F7 !important;
}

::v-deep .v-simple-table tbody tr:hover {
  background-color: #F7F7F7 !important;
}

/* Selected list item styling */
::v-deep .v-list-item-group .v-list-item--active {
  background-color: var(--color-primary) !important;
  color: white !important;
  border-radius: 4px !important;
}

::v-deep .v-list-item-group .v-list-item--active .v-list-item__title {
  color: white !important;
}

/* ===== RECONCILIATION TABLE STYLES ===== */
.reconciliation-table {
  border-radius: 4px;
  overflow: hidden;
}

.reconciliation-table table {
  table-layout: fixed;
  width: 100%;
}

/* ===== HEADER STYLING ===== */
.table-header-row th {
  font-weight: bold;
  padding: 12px 16px;
  border-bottom: 2px solid #e0e0e0;
}

/* ===== COLUMN WIDTHS ===== */
.reconciliation-table .col-description {
  width: 50%;
  text-align: left;
}

.reconciliation-table .col-sub-amount {
  width: 25%;
  text-align: right;
}

.reconciliation-table .col-final-amount {
  width: 25%;
  text-align: right;
}

/* ===== CELL PADDING ===== */
.reconciliation-table td,
.reconciliation-table th {
  padding: 8px 12px;
  border-bottom: 1px solid #e0e0e0;
}

/* ===== TABLE 1 TOTAL ROW STYLING ===== */
::v-deep .table-total-row,
.table-total-row {
  background-color: #FFF3E0 !important;
}

::v-deep .table-total-row td,
.table-total-row td {
  background-color: #FFF3E0 !important;
}

::v-deep .table-total-row:hover,
.table-total-row:hover {
  background-color: #FFE0B2 !important;
}

::v-deep .table-total-row:hover td,
.table-total-row:hover td {
  background-color: #FFE0B2 !important;
}

.table-total-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  background-color: #E65100;
  color: #FFFFFF;
  font-size: 11px;
  font-weight: 600;
  border-radius: 4px;
  vertical-align: middle;
}

/* ===== GROUP HEADER ROW STYLING ===== */
::v-deep .group-header-row,
.group-header-row {
  background-color: #ECEFF1 !important;
}

::v-deep .group-header-row td,
.group-header-row td {
  background-color: #ECEFF1 !important;
}

.group-header-cell {
  font-weight: 600;
  font-size: 13px;
  color: #37474F;
  padding: 10px 12px !important;
}

/* ===== GROUP ITEM INDENT ===== */
.group-item-indent {
  padding-left: 24px !important;
}

/* ===== TABLE 2 TOTAL ROW STYLING ===== */
::v-deep .table-2-total-row,
.table-2-total-row {
  background-color: #E3F2FD !important;
}

::v-deep .table-2-total-row td,
.table-2-total-row td {
  background-color: #E3F2FD !important;
}

::v-deep .table-2-total-row:hover,
.table-2-total-row:hover {
  background-color: #BBDEFB !important;
}

::v-deep .table-2-total-row:hover td,
.table-2-total-row:hover td {
  background-color: #BBDEFB !important;
}
</style>
